/**
 * EXTRACTION INDEX QUIZ
 * =====================
 * React komponenta za EI 3.2 upitnik
 * 
 * Implementira dvofazno mjerenje vremena:
 * 1. Faza čitanja (slider zaključan)
 * 2. Faza odluke (slider aktivan)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

interface Question {
  id: number;
  title: string;
  context: string;
  model_a_label: string;
  model_b_label: string;
  word_count: number;
  maslow_level: number;
  model_a_description?: string;
  model_b_description?: string;
  awareness_question?: string;
  awareness_options?: string[];
}

interface ResponseData {
  q_id: number;
  slider_value: number;
  time_slider_value: number;
  awareness_answer: number;
  reading_time_ms: number;
  decision_time_ms: number;
  jitter_count: number;
  hover_cant_decide_ms: number;
  slider_changes: number;
}

interface EIResult {
  ei_score: number;
  ambivalence_score: number;
  temporal_score: number;
  consistency_score: number;
  awareness_score: number;
  mvi_score: number;
  reliability_score: number;
  archetype: string;
  dominant_biometric: string;
  maslow_profile: Record<number, number>;
  diagnosis: string;
  warnings: string[];
}

type Phase = 'intro' | 'reading' | 'deciding' | 'result';

// ═══════════════════════════════════════════════════════════════════════════
// QUESTIONS DATA (hardcoded za sada, kasnije iz API-ja)
// ═══════════════════════════════════════════════════════════════════════════

const QUESTIONS: Question[] = [
  {
    id: 1,
    title: "Porez na dohodak",
    context: "Vlada ukida prirez. Grad mora odlučiti o stopi poreza na dohodak. Proračun je napregnut inflacijom. Svaki euro koji ostane tebi, fali u zajedničkoj blagajni za vrtiće, ceste i smeće.",
    model_a_label: "Moj novac (niži porez)",
    model_b_label: "Naš grad (viši porez)",
    model_a_description: "Smanjite porez na minimum. Želim zadržati više od onoga što zaradim. Sam ću odlučiti kako trošim svoj novac.",
    model_b_description: "Zadržite trenutne stope. Spreman sam imati manju neto plaću u zamjenu za funkcionalne javne usluge i uređen grad.",
    word_count: 180,
    maslow_level: 2,
    awareness_question: "Tko po tebi NAJVIŠE profitira ako se smanji porez?",
    awareness_options: ["Svi građani jednako", "Obitelji s djecom", "Ljudi s višim primanjima", "Umirovljenici", "Ne znam"]
  },
  {
    id: 2,
    title: "Vrtići",
    context: "Ekonomska cijena vrtića (10-satni program): 500€/mj po djetetu. Roditelji plaćaju: 70€/mj. Grad subvencionira: 430€/mj po djetetu. Zagreb ima 25.000 djece u vrtićima.",
    model_a_label: "Roditelji plaćaju",
    model_b_label: "Društvo plaća",
    model_a_description: "Djeca su privatna odluka. Tko ih ima, neka ih financira. Nije pošteno da samci i umirovljenici plaćaju tuđe vrtiće.",
    model_b_description: "Vrtić je infrastruktura kao cesta. Svi plaćamo kroz porez jer svi profitiramo - roditelji rade, ekonomija raste.",
    word_count: 160,
    maslow_level: 3,
    awareness_question: "Zašto je vrtić subvencioniran?",
    awareness_options: ["Jer roditelji nemaju novca", "Jer je to socijalistički relikt", "Jer omogućuje roditeljima da rade i plaćaju porez", "Jer djeca imaju pravo na igru", "Ne znam"]
  },
  {
    id: 3,
    title: "Javni prijevoz (ZET)",
    context: "ZET godišnji proračun: 240+ milijuna €. Prihod od karata: ~50 milijuna € (20%). Subvencija grada: ~190 milijuna € (80%). Pojedinačna karta: 0.53€ (pokriva 20% troška).",
    model_a_label: "Plaćam ako koristim",
    model_b_label: "Plaćam da postoji",
    model_a_description: "Karta treba koštati ekonomsku cijenu (2.50€). Tko se vozi, plaća. Tko vozi auto, ne financira ZET.",
    model_b_description: "Javni prijevoz je infrastruktura. Svi plaćamo kroz porez - i vozači - jer svi profitiramo od manje gužve i čišćeg zraka.",
    word_count: 170,
    maslow_level: 2,
    awareness_question: "Ako ZET propadne, tko NAJVIŠE profitira?",
    awareness_options: ["Svi gube jednako", "Uber, taxi, privatni prijevoznici", "Vozači automobila", "Nitko ne profitira", "Ne znam"]
  },
  {
    id: 4,
    title: "Zdravstvo",
    context: "Liste čekanja su duge (MR: 6 mjeseci, UZV: 4 mjeseca). Sustav je skup i spor. Liječnici odlaze u privatni sektor. Privatna klinika: za 3 dana. Državna bolnica: 4 mjeseca.",
    model_a_label: "Moje zdravlje (privatno)",
    model_b_label: "Javno zdravstvo",
    model_a_description: "Smanjite obvezne doprinose za 50%. Ostalo ću sam. Kad trebam doktora, platit ću policu ili pregled.",
    model_b_description: "Povećajte doprinose za 10%. Zabranite liječnicima rad u privatnom i javnom istovremeno. Svi moraju čekati red.",
    word_count: 190,
    maslow_level: 2,
    awareness_question: "Tko NAJVIŠE gubi u privatizaciji zdravstva?",
    awareness_options: ["Liječnici", "Kronični bolesnici i stariji", "Mladi i zdravi", "Privatne klinike", "Ne znam"]
  },
  {
    id: 5,
    title: "Stanovanje",
    context: "Cijena kvadrata u Zagrebu: 3.000€+. Najam stana: 700€+. Mladi ne mogu kupiti ni unajmiti. Stanovi služe kao štednja i investicija.",
    model_a_label: "Moje pravo na profit",
    model_b_label: "Pravo na dom",
    model_a_description: "Stan je privatno vlasništvo. Vlasnik određuje cijenu i namjenu. Tržište će se samo regulirati.",
    model_b_description: "Stanovanje je ljudsko pravo. Grad gradi javne stanove s ograničenom najamninom. Porez na prazne stanove je drakonski.",
    word_count: 175,
    maslow_level: 1,
    awareness_question: "Tko NAJVIŠE gubi u regulaciji najma?",
    awareness_options: ["Podstanari", "Investitori i vlasnici više stanova", "Gradski proračun", "Mladi", "Ne znam"]
  },
  {
    id: 6,
    title: "Mirovine",
    context: "Omjer radnika i umirovljenika je 1:1. Sustav je neodrživ. Prosječna mirovina je 500€.",
    model_a_label: "Moj račun (osobna štednja)",
    model_b_label: "Naša sigurnost (solidarnost)",
    model_a_description: "Ukidamo solidarnost. Koliko uplatiš, toliko imaš. Tvoja mirovina je tvoja privatna stvar i rizik.",
    model_b_description: "Mirovina je jamstvo protiv siromaštva. Bogatiji uplaćuju više da bi svi imali minimum.",
    word_count: 165,
    maslow_level: 2,
    awareness_question: "Tko u osobnom modelu mirovina prolazi najgore?",
    awareness_options: ["Bankari", "Žene i radnici s malim plaćama", "IT stručnjaci", "Država", "Ne znam"]
  },
  {
    id: 7,
    title: "Obrazovanje",
    context: "Javne škole i fakulteti su potfinancirani. Kvaliteta pada. Privatne škole nude bolje uvjete, ali koštaju (5.000€+ godišnje).",
    model_a_label: "Kvaliteta za one koji plate",
    model_b_label: "Jednake šanse za sve",
    model_a_description: "Uvedimo školarine na fakultetima. Tko želi diplomu, neka plati ili digne kredit.",
    model_b_description: "Obrazovanje mora biti besplatno od vrtića do doktorata. Financira se isključivo iz poreza.",
    word_count: 155,
    maslow_level: 4,
    awareness_question: "Što se događa s društvenom pokretljivošću u modelu školarina?",
    awareness_options: ["Raste (svatko može uspjeti)", "Pada (bogati ostaju bogati)", "Ostaje ista", "Ne znam"]
  },
  {
    id: 8,
    title: "Advent / Kultura",
    context: "Advent u Zagrebu privlači 1+ milijun posjetitelja. Pitanje je tko organizira i tko zarađuje na javnom prostoru.",
    model_a_label: "Privatni spektakl",
    model_b_label: "Javni prihod",
    model_a_description: "Dajmo sve privatnicima. Neka organiziraju, neka zarade. Grad ne troši ništa.",
    model_b_description: "Grad organizira sve sam. Možda skromnije, ali profit ide u proračun za javne usluge.",
    word_count: 145,
    maslow_level: 3,
    awareness_question: "U starom modelu Adventa (prije 2021), kamo je išao profit?",
    awareness_options: ["U gradski proračun", "Privatnim zakupcima kućica", "Turistička zajednica", "Nitko nije zarađivao", "Ne znam"]
  },
  {
    id: 9,
    title: "Ekologija",
    context: "Zagreb mora smanjiti emisije CO2 za 55% do 2030. (EU direktiva). To zahtijeva drastične mjere: zabrana automobila u centru, naknade za zagađivače, subvencije za električna vozila.",
    model_a_label: "Moja sloboda danas",
    model_b_label: "Njihova budućnost",
    model_a_description: "Ne ograničavaj moju slobodu kretanja i potrošnje. Tehnologija će riješiti problem. Neka tržište odluči.",
    model_b_description: "Plaćamo cijenu tranzicije SADA da naša djeca imaju grad. Ograničenja za zagađivače.",
    word_count: 185,
    maslow_level: 1,
    awareness_question: "Što je jeftinije?",
    awareness_options: ["Spriječiti klimatsku promjenu sada", "Prilagoditi se klimatskoj promjeni kasnije", "Isto je", "Klimatska promjena ne postoji", "Ne znam"]
  },
  {
    id: 10,
    title: "AI i digitalizacija",
    context: "Grad Zagreb želi uvesti AI asistenta za građane: pomoć s porezima, dozvolama, prijavama. Dostupan 24/7. Dva su modela na stolu.",
    model_a_label: "Besplatno (moji podaci)",
    model_b_label: "Plaćeno (moja privatnost)",
    model_a_description: "Dajmo to Google-u ili OpenAI-ju. Nama je besplatno - oni uzimaju podatke. Win-win.",
    model_b_description: "Grad gradi vlastiti sustav. Košta 20 mil € iz proračuna. Podaci ostaju u gradu.",
    word_count: 170,
    maslow_level: 5,
    awareness_question: "Zašto je Google 'besplatan'?",
    awareness_options: ["Jer su dobročinitelji", "Jer prodaju tvoje podatke oglašivačima", "Jer zarađuju na hardveru", "Jer im država plaća", "Ne znam"]
  }
];

// ═══════════════════════════════════════════════════════════════════════════
// CUSTOM HOOKS
// ═══════════════════════════════════════════════════════════════════════════

function useTimer() {
  const [time, setTime] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);

  const start = useCallback(() => {
    startTimeRef.current = Date.now();
    intervalRef.current = setInterval(() => {
      setTime(Date.now() - startTimeRef.current);
    }, 100);
  }, []);

  const stop = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    return Date.now() - startTimeRef.current;
  }, []);

  const reset = useCallback(() => {
    setTime(0);
    startTimeRef.current = 0;
  }, []);

  return { time, start, stop, reset };
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════

interface SliderProps {
  value: number;
  onChange: (value: number) => void;
  disabled: boolean;
  leftLabel: string;
  rightLabel: string;
  onJitter: () => void;
}

function PainSlider({ value, onChange, disabled, leftLabel, rightLabel, onJitter }: SliderProps) {
  const lastValueRef = useRef(value);
  const lastDirectionRef = useRef<'left' | 'right' | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(e.target.value);
    
    // Detekcija jittera (promjena smjera)
    if (lastValueRef.current !== newValue) {
      const direction = newValue > lastValueRef.current ? 'right' : 'left';
      if (lastDirectionRef.current && lastDirectionRef.current !== direction) {
        onJitter();
      }
      lastDirectionRef.current = direction;
      lastValueRef.current = newValue;
    }
    
    onChange(newValue);
  };

  return (
    <div className={`pain-slider ${disabled ? 'disabled' : ''}`}>
      <div className="slider-labels">
        <span className="label-left">{leftLabel}</span>
        <span className="label-right">{rightLabel}</span>
      </div>
      <div className="slider-container">
        <input
          type="range"
          min="0"
          max="100"
          value={value}
          onChange={handleChange}
          disabled={disabled}
          className="slider"
        />
        {disabled && (
          <div className="slider-lock">
            <span className="lock-icon">🔒</span>
            <span>Pročitaj pitanje</span>
          </div>
        )}
      </div>
      <div className="slider-value">{value}/100</div>
    </div>
  );
}

interface QuestionCardProps {
  question: Question;
  phase: 'reading' | 'deciding';
  onReadComplete: () => void;
  onSubmit: (data: Partial<ResponseData>) => void;
  readingTime: number;
}

function QuestionCard({ question, phase, onReadComplete, onSubmit, readingTime }: QuestionCardProps) {
  const [sliderValue, setSliderValue] = useState(50);
  const [timeSliderValue, setTimeSliderValue] = useState(50);
  const [awarenessAnswer, setAwarenessAnswer] = useState(-1);
  const [jitterCount, setJitterCount] = useState(0);
  const [hoverTime, setHoverTime] = useState(0);
  const [sliderChanges, setSliderChanges] = useState(0);
  const [cantDecide, setCantDecide] = useState(false);
  
  const hoverStartRef = useRef<number | null>(null);
  const decisionTimer = useTimer();

  // Reset state when question changes
  useEffect(() => {
    setSliderValue(50);
    setTimeSliderValue(50);
    setAwarenessAnswer(-1);
    setJitterCount(0);
    setHoverTime(0);
    setSliderChanges(0);
    setCantDecide(false);
    decisionTimer.reset();
  }, [question.id]);

  useEffect(() => {
    if (phase === 'deciding') {
      decisionTimer.start();
    }
  }, [phase]);

  const handleSliderChange = (value: number) => {
    setSliderValue(value);
    setSliderChanges(prev => prev + 1);
    setCantDecide(false);
  };

  const handleJitter = () => {
    setJitterCount(prev => prev + 1);
  };

  const handleCantDecideHover = (isHovering: boolean) => {
    if (isHovering) {
      hoverStartRef.current = Date.now();
    } else if (hoverStartRef.current) {
      setHoverTime(prev => prev + (Date.now() - hoverStartRef.current!));
      hoverStartRef.current = null;
    }
  };

  const handleCantDecide = () => {
    setCantDecide(true);
    handleSubmit(-1);
  };

  const handleSubmit = (overrideSlider?: number) => {
    const decisionTime = decisionTimer.stop();
    
    onSubmit({
      q_id: question.id,
      slider_value: overrideSlider !== undefined ? overrideSlider : sliderValue,
      time_slider_value: timeSliderValue,
      awareness_answer: awarenessAnswer,
      reading_time_ms: readingTime,
      decision_time_ms: decisionTime,
      jitter_count: jitterCount,
      hover_cant_decide_ms: hoverTime,
      slider_changes: sliderChanges
    });
  };

  const canSubmit = awarenessAnswer >= 0 && !cantDecide;

  return (
    <div className="question-card">
      <div className="question-header">
        <span className="question-number">Pitanje {question.id}/10</span>
        <span className="maslow-badge">Maslow: {question.maslow_level}</span>
      </div>

      <h2 className="question-title">{question.title}</h2>
      
      <div className={`question-context ${phase === 'deciding' ? 'faded' : ''}`}>
        <p>{question.context}</p>
      </div>

      {phase === 'reading' && (
        <div className="reading-phase">
          <div className="models-preview">
            <div className="model model-a">
              <h4>Model A: {question.model_a_label}</h4>
              <p>{question.model_a_description}</p>
            </div>
            <div className="model model-b">
              <h4>Model B: {question.model_b_label}</h4>
              <p>{question.model_b_description}</p>
            </div>
          </div>
          
          <div className="reading-timer">
            Vrijeme čitanja: {(readingTime / 1000).toFixed(1)}s
          </div>
          
          <button 
            className="btn btn-primary"
            onClick={onReadComplete}
          >
            Pročitao/la sam, želim odgovoriti →
          </button>
        </div>
      )}

      {phase === 'deciding' && (
        <div className="deciding-phase">
          <div className="slider-section">
            <h3>Tvoj izbor:</h3>
            <PainSlider
              value={sliderValue}
              onChange={handleSliderChange}
              disabled={false}
              leftLabel={question.model_a_label}
              rightLabel={question.model_b_label}
              onJitter={handleJitter}
            />
            
            <button 
              className="btn btn-ghost cant-decide"
              onClick={handleCantDecide}
              onMouseEnter={() => handleCantDecideHover(true)}
              onMouseLeave={() => handleCantDecideHover(false)}
            >
              ☐ Ne mogu / ne želim odabrati
            </button>
          </div>

          <div className="slider-section">
            <h3>Vremenska preferencija:</h3>
            <PainSlider
              value={timeSliderValue}
              onChange={setTimeSliderValue}
              disabled={false}
              leftLabel="Bitna mi je sadašnjost"
              rightLabel="Bitna mi je budućnost"
              onJitter={() => {}}
            />
          </div>

          <div className="awareness-section">
            <h3>{question.awareness_question}</h3>
            <div className="awareness-options">
              {question.awareness_options?.map((option, index) => (
                <label key={index} className="awareness-option">
                  <input
                    type="radio"
                    name="awareness"
                    checked={awarenessAnswer === index}
                    onChange={() => setAwarenessAnswer(index)}
                  />
                  <span>{option}</span>
                </label>
              ))}
            </div>
          </div>

          <button 
            className="btn btn-primary submit-btn"
            onClick={() => handleSubmit()}
            disabled={!canSubmit}
          >
            {canSubmit ? 'Dalje →' : 'Odaberi odgovor na pitanje o svjesnosti'}
          </button>
        </div>
      )}
    </div>
  );
}

interface ResultsProps {
  result: EIResult;
  onRestart: () => void;
}

function Results({ result, onRestart }: ResultsProps) {
  const getArchetypeColor = (archetype: string) => {
    const colors: Record<string, string> = {
      'Libertarijanac': '#ef4444',
      'Solidarist': '#22c55e',
      'Švercer': '#f59e0b',
      'Idealist': '#8b5cf6',
      'Pragmatik': '#6b7280',
      'Aktivni Agnostik': '#06b6d4',
      'Nesvjesni Ekstraktor': '#ec4899',
      'Mjesečar': '#ef4444',
      'Random Kliker': '#9ca3af'
    };
    return colors[archetype] || '#6b7280';
  };

  return (
    <div className="results-container">
      <h1>Tvoj Extraction Index Profil</h1>
      
      <div className="archetype-card" style={{ borderColor: getArchetypeColor(result.archetype) }}>
        <h2 style={{ color: getArchetypeColor(result.archetype) }}>
          {result.archetype}
        </h2>
        <p className="diagnosis">{result.diagnosis}</p>
      </div>

      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-value">{result.ei_score}</div>
          <div className="metric-label">EI Score</div>
          <div className="metric-bar">
            <div 
              className="metric-fill" 
              style={{ width: `${result.ei_score}%`, backgroundColor: '#3b82f6' }}
            />
          </div>
          <div className="metric-scale">
            <span>Eksternalizacija</span>
            <span>Internalizacija</span>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-value">{result.ambivalence_score}</div>
          <div className="metric-label">Ambivalencija</div>
          <div className="metric-bar">
            <div 
              className="metric-fill" 
              style={{ width: `${result.ambivalence_score}%`, backgroundColor: '#f59e0b' }}
            />
          </div>
          <div className="metric-scale">
            <span>Odlučan</span>
            <span>Superpozicija</span>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-value">{result.consistency_score}</div>
          <div className="metric-label">Konzistentnost</div>
          <div className="metric-bar">
            <div 
              className="metric-fill" 
              style={{ width: `${result.consistency_score}%`, backgroundColor: '#22c55e' }}
            />
          </div>
          <div className="metric-scale">
            <span>Kontradiktoran</span>
            <span>Koherentan</span>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-value">{result.awareness_score}</div>
          <div className="metric-label">Svjesnost</div>
          <div className="metric-bar">
            <div 
              className="metric-fill" 
              style={{ width: `${result.awareness_score}%`, backgroundColor: '#8b5cf6' }}
            />
          </div>
          <div className="metric-scale">
            <span>Nesvjestan</span>
            <span>Svjestan moći</span>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-value">{result.reliability_score}</div>
          <div className="metric-label">Pouzdanost</div>
          <div className="metric-bar">
            <div 
              className="metric-fill" 
              style={{ width: `${result.reliability_score}%`, backgroundColor: '#06b6d4' }}
            />
          </div>
          <div className="metric-scale">
            <span>Nepouzdan</span>
            <span>Pouzdan</span>
          </div>
        </div>

        <div className="metric-card warning">
          <div className="metric-value">{result.mvi_score}</div>
          <div className="metric-label">Rizik manipulacije (MVI)</div>
          <div className="metric-bar">
            <div 
              className="metric-fill" 
              style={{ width: `${result.mvi_score}%`, backgroundColor: '#ef4444' }}
            />
          </div>
          <div className="metric-scale">
            <span>Otporan</span>
            <span>Ranjiv</span>
          </div>
        </div>
      </div>

      {result.warnings.length > 0 && (
        <div className="warnings-section">
          <h3>⚠️ Upozorenja</h3>
          {result.warnings.map((warning, index) => (
            <div key={index} className="warning-item">{warning}</div>
          ))}
        </div>
      )}

      <div className="maslow-section">
        <h3>📊 Maslow profil</h3>
        <div className="maslow-chart">
          {Object.entries(result.maslow_profile).map(([level, score]) => {
            const labels: Record<string, string> = {
              '1': 'Fiziološke',
              '2': 'Sigurnost', 
              '3': 'Pripadanje',
              '4': 'Status',
              '5': 'Vrh'
            };
            return (
              <div key={level} className="maslow-bar">
                <span className="maslow-label">{labels[level]}</span>
                <div className="maslow-track">
                  <div 
                    className="maslow-fill"
                    style={{ width: `${score}%` }}
                  />
                </div>
                <span className="maslow-value">{score.toFixed(0)}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="biometric-section">
        <h3>🧠 Biometrijski profil</h3>
        <p>Dominantni tip: <strong>{result.dominant_biometric}</strong></p>
      </div>

      <button className="btn btn-primary" onClick={onRestart}>
        Ponovi test
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export default function EIQuiz() {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [phase, setPhase] = useState<Phase>('intro');
  const [responses, setResponses] = useState<ResponseData[]>([]);
  const [result, setResult] = useState<EIResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [_error, setError] = useState<string | null>(null);
  
  const readingTimer = useTimer();

  const currentQuestion = QUESTIONS[currentQuestionIndex];

  const startQuiz = () => {
    setPhase('reading');
    readingTimer.start();
  };

  const handleReadComplete = () => {
    readingTimer.stop();
    setPhase('deciding');
  };

  const handleQuestionSubmit = async (data: Partial<ResponseData>) => {
    const fullResponse: ResponseData = {
      q_id: data.q_id!,
      slider_value: data.slider_value!,
      time_slider_value: data.time_slider_value!,
      awareness_answer: data.awareness_answer!,
      reading_time_ms: data.reading_time_ms!,
      decision_time_ms: data.decision_time_ms!,
      jitter_count: data.jitter_count!,
      hover_cant_decide_ms: data.hover_cant_decide_ms!,
      slider_changes: data.slider_changes || 0
    };

    const newResponses = [...responses, fullResponse];
    setResponses(newResponses);

    if (currentQuestionIndex < QUESTIONS.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setPhase('reading');
      readingTimer.reset();
      readingTimer.start();
    } else {
      // Završeno - pošalji na API
      await submitToAPI(newResponses);
    }
  };

  const submitToAPI = async (allResponses: ResponseData[]) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/calculate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ responses: allResponses })
      });

      if (!response.ok) {
        throw new Error('Greška pri izračunu');
      }

      const data = await response.json();
      setResult(data.result);
      setPhase('result');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Nepoznata greška');
      // Fallback - lokalni izračun (za development)
      console.error('API error, using mock result');
      setResult({
        ei_score: 55,
        ambivalence_score: 30,
        temporal_score: 60,
        consistency_score: 65,
        awareness_score: 70,
        mvi_score: 35,
        reliability_score: 85,
        archetype: 'Pragmatik',
        dominant_biometric: 'analyst',
        maslow_profile: { 1: 50, 2: 55, 3: 60, 4: 65, 5: 70 },
        diagnosis: 'Ti si PRAGMATIK. Procjenjuješ svaku situaciju zasebno.',
        warnings: []
      });
      setPhase('result');
    } finally {
      setLoading(false);
    }
  };

  const handleRestart = () => {
    setCurrentQuestionIndex(0);
    setPhase('intro');
    setResponses([]);
    setResult(null);
    setError(null);
    readingTimer.reset();
  };

  if (phase === 'intro') {
    return (
      <div className="ei-quiz intro">
        <h1>Extraction Index 3.0</h1>
        <p className="subtitle">Biometrijski senzor mišljenja</p>
        
        <div className="intro-content">
          <p>
            Ovaj alat mjeri tvoju poziciju na spektru između 
            <strong> eksternalizacije</strong> (prebacuješ troškove na druge) i 
            <strong> internalizacije</strong> (preuzimаš troškove na sebe).
          </p>
          
          <p>
            Nema točnih i netočnih odgovora. Ali postoje <strong>konzistentni</strong> i 
            <strong> kontradiktorni</strong> odgovori.
          </p>

          <div className="warning-box">
            <strong>⚠️ Biometrija:</strong> Mjerimo vrijeme čitanja i odlučivanja. 
            Ako preskačeš pitanja, to ćemo vidjeti.
          </div>

          <p>Test traje 10-15 minuta. Trebaš odgovoriti na 10 pitanja.</p>
        </div>

        <button className="btn btn-primary btn-large" onClick={startQuiz}>
          Započni test →
        </button>
      </div>
    );
  }

  if (phase === 'result' && result) {
    return <Results result={result} onRestart={handleRestart} />;
  }

  if (loading) {
    return (
      <div className="ei-quiz loading">
        <div className="spinner" />
        <p>Analiziram tvoje odgovore...</p>
      </div>
    );
  }

  return (
    <div className="ei-quiz">
      <div className="progress-bar">
        <div 
          className="progress-fill"
          style={{ width: `${((currentQuestionIndex + 1) / QUESTIONS.length) * 100}%` }}
        />
      </div>
      
      <QuestionCard
        question={currentQuestion}
        phase={phase as 'reading' | 'deciding'}
        onReadComplete={handleReadComplete}
        onSubmit={handleQuestionSubmit}
        readingTime={readingTimer.time}
      />
    </div>
  );
}
